/** biome-ignore-all lint/a11y/noStaticElementInteractions: pointer gestures are intentionally handled on the presentation layer */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: taps are forwarded to the gallery's existing pointer-accessible controls */

"use client";

import { clamp, easeOut } from "@/lib/utils";
import { type CSSProperties, forwardRef, type MouseEvent as ReactMouseEvent, type ReactNode, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
const AXIS_LOCK_DISTANCE = 6;
const VERTICAL_AXIS_TOLERANCE = 0.7;
const HORIZONTAL_ABORT_RATIO = 1.4;
const MODAL_ANIMATION_DURATION = 400;
const SPRING_STIFFNESS = 250;
const SPRING_DAMPING = 28;
const DEFAULT_THRESHOLD = 0.24;
const DEFAULT_VELOCITY_THRESHOLD = 1.1;
const DEFAULT_RESISTANCE = 0.85;
const DISMISS_UNMOUNT_PROGRESS = 0.9;
export type InteractiveDismissHandle = {
  dismiss: () => void;
};
type InteractiveDismissProps = {
  children: ReactNode;
  onDismiss: () => void;
  onTap?: (event: ReactMouseEvent<HTMLDivElement>) => void;
  horizontalScrollRef?: React.RefObject<HTMLDivElement | null>;
  threshold?: number;
  velocityThreshold?: number;
  resistance?: number;
  disableAnimations?: boolean;
  className?: string;
};
function resistedTranslation(distance: number, viewportHeight: number, resistance: number) {
  return viewportHeight * (1 - Math.exp(-distance / (viewportHeight * resistance)));
}
const ModalDrawer = forwardRef<InteractiveDismissHandle, InteractiveDismissProps>(function InteractiveDismiss({
  children,
  onDismiss,
  onTap,
  horizontalScrollRef,
  threshold = DEFAULT_THRESHOLD,
  velocityThreshold = DEFAULT_VELOCITY_THRESHOLD,
  resistance = DEFAULT_RESISTANCE,
  disableAnimations = false,
  className
}, ref) {
  const presentationRef = useRef<HTMLDivElement>(null);
  const entranceRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
    mode: "undecided" | "horizontal" | "vertical";
  } | null>(null);
  const frameRef = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);
  const didDragRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const floatingRef = useRef(false);
  const entranceAnimationRef = useRef<Animation | null>(null);
  const scrollLockRef = useRef<{
    overflowX: string;
    scrollSnapType: string;
  } | null>(null);
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => {
      reducedMotionRef.current = mediaQuery.matches;
    };
    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);
  const stopEntranceAnimation = useCallback(() => {
    if (entranceAnimationRef.current !== null) {
      entranceAnimationRef.current.cancel();
      entranceAnimationRef.current = null;
    }
    const entrance = entranceRef.current;
    if (entrance) {
      entrance.style.opacity = "1";
      entrance.style.transform = "translate3d(0, 0, 0) scale(1)";
    }
  }, []);
  useImperativeHandle(ref, () => ({
    dismiss: () => {
      stopEntranceAnimation();
      floatPresentation();
      animateRelease(0, window.innerHeight * 1.05, true, window.innerHeight * 1.8);
    }
  }));
  useLayoutEffect(() => {
    if (disableAnimations || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      stopEntranceAnimation();
      return;
    }
    const entrance_0 = presentationRef.current;
    if (!entrance_0) return;
    const keyframes = new KeyframeEffect(entrance_0, [{
      opacity: 0.5,
      transform: "translate3d(0, 200px, 0) scale(0.9)"
    }, {
      opacity: 1,
      transform: "translate3d(0, 0, 0) scale(1)"
    }], {
      duration: MODAL_ANIMATION_DURATION,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      fill: "both"
    });
    const animation = new Animation(keyframes, document.timeline);
    entranceAnimationRef.current = animation;
    animation.play();
    return () => {
      stopEntranceAnimation();
    };
  }, [disableAnimations, stopEntranceAnimation]);
  useEffect(() => {
    return () => {
      entranceAnimationRef.current?.cancel();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    };
  }, []);
  function lockHorizontalScroll() {
    const scroller = horizontalScrollRef?.current;
    if (!scroller || scrollLockRef.current) return;
    scrollLockRef.current = {
      overflowX: scroller.style.overflowX,
      scrollSnapType: scroller.style.scrollSnapType
    };
    scroller.style.overflowX = "hidden";
    scroller.style.scrollSnapType = "none";
  }
  function unlockHorizontalScroll() {
    const scroller_0 = horizontalScrollRef?.current;
    const previousStyles = scrollLockRef.current;
    if (!scroller_0 || !previousStyles) return;
    scroller_0.style.overflowX = previousStyles.overflowX;
    scroller_0.style.scrollSnapType = previousStyles.scrollSnapType;
    scrollLockRef.current = null;
  }
  function renderPresentation(translateY: number, viewportHeight: number) {
    const progress = clamp(translateY / viewportHeight, 0, 1);
    const easedProgress = easeOut(progress);
    const scale = 1 - easedProgress * 0.4;
    const radius = easedProgress * 30;
    const opacity = 1 - easedProgress * 1.5;
    const presentation = presentationRef.current;
    if (presentation) {
      presentation.style.transform = `translate3d(0, ${translateY}px, 0) scale(${scale})`;
      presentation.style.borderRadius = `${radius}px`;
      presentation.style.opacity = String(opacity);
    }
  }
  function stopAnimation() {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }
  function cancelReleaseAnimation() {
    stopAnimation();
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    restorePresentation();
    unlockHorizontalScroll();
  }
  function floatPresentation() {
    const presentation_0 = presentationRef.current;
    if (!presentation_0 || floatingRef.current) return;
    const rect = presentation_0.getBoundingClientRect();
    presentation_0.style.position = "fixed";
    presentation_0.style.top = `${rect.top}px`;
    presentation_0.style.left = `${rect.left}px`;
    presentation_0.style.width = `${rect.width}px`;
    presentation_0.style.height = `${rect.height}px`;
    presentation_0.style.zIndex = "15";
    floatingRef.current = true;
  }
  function restorePresentation() {
    const presentation_1 = presentationRef.current;
    if (!presentation_1 || !floatingRef.current) return;
    presentation_1.style.position = "";
    presentation_1.style.top = "";
    presentation_1.style.left = "";
    presentation_1.style.width = "";
    presentation_1.style.height = "";
    presentation_1.style.zIndex = "";
    floatingRef.current = false;
  }
  function animateRelease(startY: number, targetY: number, shouldDismiss: boolean, initialVelocity = 0) {
    stopAnimation();
    const viewportHeight_0 = window.innerHeight;
    const presentation_2 = presentationRef.current;
    if (!presentation_2) return;
    if (disableAnimations || reducedMotionRef.current) {
      if (shouldDismiss) {
        renderPresentation(viewportHeight_0 * 1.05, viewportHeight_0);
        onDismiss();
      } else {
        renderPresentation(0, viewportHeight_0);
        restorePresentation();
        unlockHorizontalScroll();
      }
      return;
    }
    let position = startY;
    let velocity = initialVelocity;
    let previousTime = performance.now();
    const animate = (time: number) => {
      const delta = Math.min((time - previousTime) / 1000, 0.032);
      previousTime = time;
      const displacement = targetY - position;
      velocity += displacement * SPRING_STIFFNESS * delta;
      velocity *= Math.exp(-SPRING_DAMPING * delta);
      position += velocity * delta;
      renderPresentation(position, viewportHeight_0);
      if (shouldDismiss && position >= viewportHeight_0 * DISMISS_UNMOUNT_PROGRESS) {
        animationRef.current = null;
        onDismiss();
        return;
      }
      const settled = Math.abs(targetY - position) < 0.5 && Math.abs(velocity) < 0.03;
      if (!settled) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      animationRef.current = null;
      renderPresentation(targetY, viewportHeight_0);
      restorePresentation();
      unlockHorizontalScroll();
      if (shouldDismiss) onDismiss();
    };
    animationRef.current = requestAnimationFrame(animate);
  }
  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    stopEntranceAnimation();
    cancelReleaseAnimation();
    didDragRef.current = false;
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      mode: "undecided"
    };
  }
  function handlePointerMove(event_0: ReactPointerEvent<HTMLDivElement>) {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event_0.pointerId) return;
    const deltaX = event_0.clientX - gesture.startX;
    const deltaY = event_0.clientY - gesture.startY;
    if (gesture.mode === "undecided") {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < AXIS_LOCK_DISTANCE) {
        return;
      }
      gesture.mode = Math.abs(deltaY) >= Math.abs(deltaX) * VERTICAL_AXIS_TOLERANCE ? "vertical" : "horizontal";
      if (gesture.mode === "horizontal") {
        gestureRef.current = null;
        restorePresentation();
        unlockHorizontalScroll();
        return;
      }
      floatPresentation();
      lockHorizontalScroll();
      event_0.currentTarget.setPointerCapture(event_0.pointerId);
    }
    if (gesture.mode !== "vertical") return;
    if (Math.abs(deltaX) > Math.abs(deltaY) * HORIZONTAL_ABORT_RATIO && Math.abs(deltaX) >= AXIS_LOCK_DISTANCE) {
      gestureRef.current = null;
      didDragRef.current = true;
      if (event_0.currentTarget.hasPointerCapture(event_0.pointerId)) {
        event_0.currentTarget.releasePointerCapture(event_0.pointerId);
      }
      const currentY = deltaY > 0 ? resistedTranslation(deltaY, window.innerHeight, resistance) : 0;
      animateRelease(currentY, 0, false, currentY > 0 ? gesture.velocity * 1000 : 0);
      return;
    }
    didDragRef.current = true;
    const now = event_0.timeStamp || performance.now();
    gesture.velocity = (event_0.clientY - gesture.lastY) / Math.max(now - gesture.lastTime, 1);
    gesture.lastY = event_0.clientY;
    gesture.lastTime = now;
    if (deltaY <= 0) {
      renderPresentation(0, window.innerHeight);
      return;
    }
    event_0.preventDefault();
    const viewportHeight_1 = window.innerHeight;
    const resistedY = resistedTranslation(deltaY, viewportHeight_1, resistance);
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      renderPresentation(resistedY, viewportHeight_1);
      frameRef.current = null;
    });
  }
  function finishGesture(event_1: ReactPointerEvent<HTMLDivElement>) {
    const gesture_0 = gestureRef.current;
    if (!gesture_0 || gesture_0.pointerId !== event_1.pointerId) return;
    gestureRef.current = null;
    if (event_1.currentTarget.hasPointerCapture(event_1.pointerId)) {
      event_1.currentTarget.releasePointerCapture(event_1.pointerId);
    }
    if (gesture_0.mode !== "vertical") return;
    const distance = event_1.clientY - gesture_0.startY;
    const shouldDismiss_0 = distance / window.innerHeight > threshold || distance > 0 && gesture_0.velocity > velocityThreshold;
    const currentY_0 = distance > 0 ? window.innerHeight * (1 - Math.exp(-distance / (window.innerHeight * resistance))) : 0;
    animateRelease(currentY_0, shouldDismiss_0 ? window.innerHeight * 1.05 : 0, shouldDismiss_0, currentY_0 > 0 ? gesture_0.velocity * 1000 : 0);
  }
  function handleClick(event_2: ReactMouseEvent<HTMLDivElement>) {
    if (didDragRef.current) {
      event_2.stopPropagation();
      didDragRef.current = false;
      return;
    }
    onTap?.(event_2);
  }
  const style: CSSProperties = {
    touchAction: "pan-x",
    transform: "translate3d(0, 0, 0) scale(1)",
    transformOrigin: "center center",
    willChange: "transform, border-radius"
  };
  return <div ref={presentationRef} className={className} style={style} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={finishGesture} onPointerCancel={finishGesture} onClick={handleClick}>
			<div ref={entranceRef}>{children}</div>
		</div>;
});
export default ModalDrawer;